/**
 * PlayGame Component
 * Main wrapper component for the Unity game
 * Handles game display and navigation
 */

import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { GameParams } from '../../../libs/GameHelper'
import { PlayGameUnity } from './PlayGameUnity'
import { PageWrapper } from '../../../shared/components'
import { GameDesktopOnlyModal } from './GameDesktopOnlyModal'
import { useMobileGameGuard } from '../hooks/useMobileGameGuard'
import { FacilitatorRoutes, GamerRoutes } from '../../../config/routes/routeEnums'

interface PlayGameProps {
  gameParams?: Partial<GameParams>
  className?: string
}

/**
 * PlayGame - Main game component
 * Wraps the Unity game player and provides navigation.
 * Develop-mode affordances live inside PlayGameUnity, gated on config.enableDevelop
 * (which already folds in the ?enableDevelop= URL override).
 */
export const PlayGame: React.FC<PlayGameProps> = ({ gameParams, className = '' }) => {
  const { isMobile } = useMobileGameGuard()
  const navigate = useNavigate()
  const location = useLocation()

  // Game activities are desktop-only. This is the single choke point for EVERY path onto the
  // game page (enter-lobby buttons, game center, onboarding tutorial, direct URL entry) — on a
  // phone/tablet the Unity game and its Whereby lobby never mount; a blocking notice shows instead.
  if (isMobile) {
    const exitRoute = location.pathname.startsWith(FacilitatorRoutes.BASE)
      ? FacilitatorRoutes.DASHBOARD
      : GamerRoutes.UPCOMING_ACTIVITIES

    return (
      <PageWrapper
        className={className}
        sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <GameDesktopOnlyModal isOpen onClose={() => navigate(exitRoute)} />
      </PageWrapper>
    )
  }

  return (
    <PageWrapper
      className={className}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <PlayGameUnity gameParams={gameParams} />
    </PageWrapper>
  )
}
